
import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface RippleProps {
  /**
   * Color of the ripple effect
   * @default "rgba(255, 255, 255, 0.6)"
   */
  color?: string;
  /**
   * Duration of the ripple animation in milliseconds
   * @default 600
   */
  duration?: number;
  /**
   * Scale factor for the ripple
   * @default 4
   */
  scale?: number;
  /**
   * CSS class to apply to the ripple container
   */
  className?: string;
}

export const Ripple: React.FC<RippleProps> = ({
  color = "rgba(255, 255, 255, 0.6)",
  duration = 600,
  scale = 4,
  className,
}) => {
  const [ripples, setRipples] = useState<Array<{
    x: number;
    y: number;
    size: number;
    id: number;
  }>>([]);
  
  const nextId = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handlePointerDown = (event: PointerEvent) => {
      const rect = container.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      // Make ripple larger for more visibility
      const size = Math.max(container.offsetWidth, container.offsetHeight) * 1.5;
      
      const newRipple = {
        x,
        y,
        size,
        id: nextId.current,
      };
      
      nextId.current += 1;
      setRipples(prev => [...prev, newRipple]);
      
      // Clean up ripple after animation completes
      setTimeout(() => {
        setRipples(prev => prev.filter(ripple => ripple.id !== newRipple.id));
      }, duration);
    };

    container.addEventListener('pointerdown', handlePointerDown);
    
    return () => {
      container.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [duration]);

  return (
    <div 
      ref={containerRef} 
      className={cn("absolute inset-0 overflow-hidden pointer-events-none", className)}
      style={{ zIndex: 5 }} // Explicitly set z-index
    >
      {ripples.map(ripple => (
        <span
          key={ripple.id}
          className="absolute rounded-full block"
          style={{
            left: ripple.x - ripple.size / 2,
            top: ripple.y - ripple.size / 2,
            width: ripple.size,
            height: ripple.size,
            backgroundColor: color,
            animation: `ripple ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`,
            opacity: "1", // Increased opacity for better visibility
            transform: "scale(0)",
            zIndex: 10,
            pointerEvents: "none", // Ensure it doesn't block interactions
          }}
        />
      ))}
    </div>
  );
};
